import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Modal,
  ModalHeader,
  ModalHeaderTitle,
  ModalBody,
  ModalFooter,
  TextArea,
  notification,
} from '@antoree/ant-ui';
import {
  FileImageOutlined,
  RobotOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { AlertTriangleIcon } from '../Chat/utils';
import * as homeworkService from '../../services/homework/homeworkService';
import { useToast } from '../../components/ToastProvider';
import styles from './HomeworkAssignmentModal.module.scss';

// Moving helper to top and using function declaration for hoisting
function AntoreeCustomizeLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
      <LoadingOutlined style={{ fontSize: 48, color: '#14B24C' }} spin />
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  unitId: string | number;
  studentId: string | number;
  teacherId: string | number;
  onSuccess?: (homeworkId: string | number) => void;
  initialFile?: File | null;
}

const HomeworkAssignmentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  unitId,
  studentId,
  teacherId,
  onSuccess,
  initialFile = null,
}) => {
  const { showToast } = useToast();
  const [step, setStep] = useState<'upload' | 'generate' | 'edit'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [
    content,
    setContent,
  ] = useState<homeworkService.HomeworkContent | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Track if we've already triggered generation to prevent retries
  const hasTriggeredRef = React.useRef(false);

  useEffect(() => {
    if (isOpen) {
      // Always reset content when modal opens
      setContent(null);
      setIsGenerating(false);
      hasTriggeredRef.current = false; // Reset trigger flag

      if (initialFile) {
        // Generate from file (video or image/PDF)
        setFile(initialFile);
        setStep('generate');
        // We'll trigger the generation in a separate effect once file state is updated
      } else {
        setStep('upload');
        setFile(null);
      }
    } else {
      // Reset everything when modal closes
      setStep('upload');
      setFile(null);
      setContent(null);
      setIsGenerating(false);
      hasTriggeredRef.current = false; // Reset trigger flag
    }
  }, [isOpen, initialFile]);

  useEffect(() => {
    // Only trigger once per modal open
    if (hasTriggeredRef.current) {
      return;
    }

    // Generate from file (video or image/PDF)
    if (isOpen && file && step === 'generate' && !isGenerating && !content) {
      hasTriggeredRef.current = true; // Mark as triggered
      handleGenerate();
    }
  }, [isOpen, file, step, isGenerating, content]);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        showToast({
          message: 'File quá lớn. Vui lòng chọn file dưới 10MB',
          type: 'error',
        });
        e.target.value = ''; // Reset input
        return;
      }

      // Validate file is not empty
      if (selectedFile.size === 0) {
        showToast({
          message: 'File rỗng. Vui lòng chọn file có nội dung',
          type: 'error',
        });
        e.target.value = ''; // Reset input
        return;
      }

      // Validate file type
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ];

      const allowedExtensions = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.webp',
        '.pdf',
        '.ppt',
        '.pptx',
      ];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .substring(selectedFile.name.lastIndexOf('.'));

      if (
        !allowedTypes.includes(selectedFile.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        showToast({
          message:
            'Định dạng file không được hỗ trợ. Vui lòng chọn file: JPG, PNG, PDF, PPT, PPTX',
          type: 'error',
        });
        e.target.value = ''; // Reset input
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleGenerate = async () => {
    // Validate file exists
    if (!file) {
      showToast({
        message: 'Vui lòng chọn file để tiếp tục',
        type: 'error',
      });
      return;
    }

    // Validate file size - skip for videos (can be large)
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 2 * 1024 * 1024 * 1024 : 10 * 1024 * 1024; // 2GB for video, 10MB for others

    if (file.size > maxSize) {
      showToast({
        message: isVideo
          ? 'Video quá lớn. Vui lòng chọn video dưới 2GB'
          : 'File quá lớn. Vui lòng chọn file dưới 10MB',
        type: 'error',
      });
      return;
    }

    // Validate file is not empty
    if (file.size === 0) {
      showToast({
        message: 'File rỗng. Vui lòng chọn file có nội dung',
        type: 'error',
      });
      return;
    }

    setIsGenerating(true);

    try {
      let response;

      // Check if file is video
      if (isVideo) {
        // Call video API
        response = await homeworkService.generateHomeworkFromVideo({
          file,
        });
      } else {
        // Call image/PDF API
        response = await homeworkService.generateHomeworkFromImage({
          file,
          ageGroup: 'Teen',
          numQuestions: 10,
        });
      }

      if (response.data?.data?.content) {
        const generatedContent = response.data.data.content;
        setContent({
          ...generatedContent,
          level: generatedContent.level || 'A2',
        });
        setStep('edit');

        // Show success notification
        notification.success({
          title: (
            <FormattedMessage
              id="homework.generate_content_success"
              defaultMessage="Homework content generated successfully"
            />
          ),
        });
      } else {
        throw new Error('Invalid response from AI');
      }
    } catch (error: any) {
      showToast({
        message: isVideo
          ? 'Không thể tạo bài tập từ video này. Vui lòng thử lại.'
          : 'Không thể tạo bài tập từ ảnh này. Vui lòng thử lại.',
        type: 'error',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!content) return;

    setIsSaving(true);
    try {
      const saveData: homeworkService.HomeworkSaveData = {
        unitId: Number(unitId),
        studentId: Number(studentId),
        teacherId: Number(teacherId),
        status: 'assigned',
        content: {
          topic: content.topic,
          level: content.level || 'A2',
          sections: content.sections,
        },
        configSnapshot: {
          topic: content.topic,
          level: content.level || 'A2',
          source: 'hybrid_gemini_gpt4o_mini',
        },
        source: 'web', // Track that assignment message comes from web
      };

      const response = await homeworkService.saveHomework(saveData);

      if (response.data && response.data.data && response.data.data.id) {
        if (onSuccess) onSuccess(response.data.data.id);
        onClose();
        // Reset state
        setStep('upload');
        setFile(null);
        setContent(null);
      }
    } catch (error: any) {
      showToast({ message: 'Lỗi khi lưu bài tập', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const updateQuestion = (
    sectionIdx: number,
    questionIdx: number,
    field: string,
    value: any,
  ) => {
    if (!content) return;
    const newContent = { ...content };
    const section = newContent.sections[sectionIdx];
    const question = section.questions[questionIdx] as any;
    question[field] = value;
    setContent(newContent);
  };

  const updateOption = (
    sectionIdx: number,
    questionIdx: number,
    optionIdx: number,
    value: string,
  ) => {
    if (!content) return;
    const newContent = { ...content };
    newContent.sections[sectionIdx].questions[questionIdx].options[
      optionIdx
    ] = value;
    setContent(newContent);
  };

  const removeQuestion = (sectionIdx: number, questionIdx: number) => {
    if (!content) return;
    const newContent = { ...content };
    newContent.sections[sectionIdx].questions.splice(questionIdx, 1);
    setContent(newContent);
  };

  const addQuestion = (sectionIdx: number) => {
    if (!content) return;
    const newContent = { ...content };
    newContent.sections[sectionIdx].questions.push({
      question: 'New Question',
      options: ['Option A', 'Option B', 'Option C', 'Option D'],
      answer: 0,
      explanation: '',
      skill: 'grammar',
    });
    setContent(newContent);
  };

  return (
    <Modal
      onClose={onClose}
      style={{ width: '800px' }}
      className={styles.assignmentModal}
    >
      <ModalHeader>
        <ModalHeaderTitle>
          <RobotOutlined style={{ marginRight: 8, color: '#14B24C' }} />
          AI Homework Assistant
        </ModalHeaderTitle>
      </ModalHeader>

      <ModalBody>
        {step === 'upload' && (
          <div className={styles.uploadStep}>
            <div className={styles.uploadArea}>
              <input
                type="file"
                id="homework-file"
                accept="image/*,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                onChange={handleFileChange}
                className={styles.hiddenInput}
              />
              <label htmlFor="homework-file" className={styles.uploadLabel}>
                {file ? (
                  <div className={styles.fileSelected}>
                    <FileImageOutlined
                      style={{ fontSize: 48, color: '#14B24C' }}
                    />
                    <p>{file.name}</p>
                    <span className={styles.changeFile}>Click to change</span>
                  </div>
                ) : (
                  <div className={styles.dropPlaceholder}>
                    <PlusOutlined style={{ fontSize: 48, color: '#cbd5e1' }} />
                    <p>Upload an image of a book page, PDF, or PowerPoint</p>
                    <span>Hỗ trợ: JPG, PNG, PDF, PPT, PPTX (Tối đa 10MB)</span>
                  </div>
                )}
              </label>
            </div>
          </div>
        )}

        {step === 'generate' || isGenerating ? (
          <div className={styles.loadingStep}>
            <AntoreeCustomizeLoading />
            <h3>Analysing and creating...</h3>
            <p>
              AI is processing the content from your file. This may take 15-30
              seconds.
            </p>
          </div>
        ) : null}

        {step === 'edit' && content && (
          <div className={styles.editStep}>
            {/* AI Warning Banner */}
            <div
              style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <AlertTriangleIcon size={24} color="#ffc107" />
              <div>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#856404',
                    marginBottom: '4px',
                  }}
                >
                  AI-Generated Content Warning
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#856404',
                    lineHeight: '1.5',
                  }}
                >
                  This content was automatically generated by AI and may contain
                  errors or inaccuracies. Please carefully review and verify all
                  questions, answers, and explanations before assigning to
                  students.
                </div>
              </div>
            </div>

            <div className={styles.topicHeader}>
              <div className={styles.topicField}>
                <label>Topic:</label>
                <input
                  type="text"
                  value={content.topic}
                  onChange={e =>
                    content && setContent({ ...content, topic: e.target.value })
                  }
                />
              </div>
              <div className={styles.levelField}>
                <label>Level:</label>
                <input
                  type="text"
                  value={content.level}
                  onChange={e =>
                    content && setContent({ ...content, level: e.target.value })
                  }
                  placeholder="e.g. A1, B2"
                />
              </div>
            </div>

            <div className={styles.questionsList}>
              {content.sections.map((section, sIdx) => (
                <div key={sIdx} className={styles.sectionContainer}>
                  <div className={styles.sectionHeader}>
                    <input
                      type="text"
                      value={section.title}
                      onChange={e => {
                        if (!content) return;
                        const newContent = { ...content };
                        newContent.sections[sIdx].title = e.target.value;
                        setContent(newContent);
                      }}
                    />
                  </div>

                  {section.questions.map((q, qIdx) => (
                    <div key={qIdx} className={styles.questionCard}>
                      <div className={styles.cardHeader}>
                        <span>Question {qIdx + 1}</span>
                        <Button
                          color="danger"
                          onClick={() => removeQuestion(sIdx, qIdx)}
                          className={styles.deleteBtn}
                        >
                          <DeleteOutlined />
                        </Button>
                      </div>

                      <TextArea
                        value={q.question}
                        onChange={(e: any) =>
                          updateQuestion(sIdx, qIdx, 'question', e.target.value)
                        }
                        placeholder="Question content"
                        rows={2}
                        className={styles.questionInput}
                        fullWidth
                      />

                      <div className={styles.optionsGrid}>
                        {q.options.map((opt, oIdx) => (
                          <div key={oIdx} className={styles.optionItem}>
                            <input
                              type="radio"
                              name={`ans-${sIdx}-${qIdx}`}
                              checked={q.answer === oIdx}
                              onChange={() =>
                                updateQuestion(sIdx, qIdx, 'answer', oIdx)
                              }
                            />
                            <input
                              type="text"
                              value={opt}
                              onChange={e =>
                                updateOption(sIdx, qIdx, oIdx, e.target.value)
                              }
                            />
                          </div>
                        ))}
                      </div>

                      <div className={styles.explanationArea}>
                        <label>Explanation:</label>
                        <TextArea
                          value={q.explanation}
                          onChange={(e: any) =>
                            updateQuestion(
                              sIdx,
                              qIdx,
                              'explanation',
                              e.target.value,
                            )
                          }
                          placeholder="Explanation"
                          rows={1}
                        />
                      </div>
                    </div>
                  ))}

                  <Button
                    onClick={() => addQuestion(sIdx)}
                    className={styles.dashedBtn}
                  >
                    <PlusOutlined style={{ marginRight: 8 }} />
                    Add question
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <div className={styles.footerActions}>
          <Button onClick={onClose} disabled={isSaving || isGenerating}>
            Cancel
          </Button>

          {step === 'upload' && (
            <Button
              color="primary"
              onClick={handleGenerate}
              isLoading={isGenerating}
            >
              <RobotOutlined style={{ marginRight: 8 }} />
              Generate homework using AI
            </Button>
          )}

          {step === 'edit' && (
            <div className={styles.editActions}>
              <Button onClick={() => setStep('upload')}>Back</Button>
              <Button color="primary" onClick={handleSave} isLoading={isSaving}>
                <SaveOutlined style={{ marginRight: 8 }} />
                Assign homework
              </Button>
            </div>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default HomeworkAssignmentModal;
